import { generatePath } from 'react-router-dom';
import { LinkProps } from '@/components';

type LocationDescriptor = { pathname?: string; search?: string };

/**
 * Converts a search object which may have string, numeric, boolean, or undefined values to a search string
 */
export function buildSearchString(searchObj: Record<string, string | number | boolean | undefined>) {
  const options: Record<string, string> = {};
  Object.keys(searchObj).forEach((key) => {
    if (searchObj[key] !== undefined) options[key] = `${searchObj[key]}`;
  });
  return new URLSearchParams(options).toString();
}

/**
 * A utility to parse the current route and generate a "to" route
 *
 * @param to string | LocationDescriptor
 * @param routeParams Optional map of route params to pass to the generated URL
 * @param search Optional dependency injection for the current route to match against
 *
 * @returns A fully resolved URL
 */
export const linkTo = (
  to: string,
  routeParams?: Record<string, string | number | boolean>,
  search?: string
): string => {
  const location = buildLocation({ pathname: to, search });

  return `${generatePath(location.pathname ?? to, routeParams)}${
    location.search === '' ? '' : `?${location.search.toString()}`
  }`;
};

const buildLocation = (to: LinkProps['to']) => {
  if (typeof to === 'function') {
    throw new Error('`to` as a function is not supported');
  }

  let next = {} as LocationDescriptor;

  if (typeof to === 'string') {
    const url = new URL(to, window.location.origin);
    next = {
      pathname: url.pathname,
      search: url.search,
    };
  } else {
    next = { ...to };
  }

  const searchParams = new URLSearchParams(next.search);

  return {
    ...next,
    search: searchParams.toString(),
  };
};
